import type React from 'react';
import { memo, useCallback, useMemo } from 'react';
import useCanvasHistory from '../hooks/useCanvasHistory';
import { useCanvasStore } from '../store/canvasStore';
import type { AnimationType, CanvasImage, TextItem } from '../types';

const FONT_FAMILIES = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Verdana', label: 'Verdana' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Open Sans', label: 'Open Sans' },
];

const FONT_SIZES = [12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72];
const LINE_HEIGHTS = [0.8, 1.0, 1.2, 1.4, 1.5, 1.6, 1.8, 2.0];
const LETTER_SPACINGS = [-2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2, 3, 4, 5];

const ENTRANCE_ANIMATIONS = [
  { value: 'simple-fade', label: 'Fade In' },
  { value: 'slide-from-left', label: 'Slide From Left' },
  { value: 'slide-from-right', label: 'Slide From Right' },
  { value: 'slide-from-bottom', label: 'Slide From Bottom' },
  { value: 'slide-from-top', label: 'Slide From Top' },
  { value: 'move', label: 'Move In' },
  { value: 'grow', label: 'Grow' },
  { value: 'shrink', label: 'Shrink' },
  { value: 'blur-in', label: 'Blur In' },
];

const LOOP_ANIMATIONS = [
  { value: 'fade-in', label: 'Pulsing Fade' },
  { value: 'slide-up', label: 'Floating' },
  { value: 'zoom-in', label: 'Pulsing Size' },
  { value: 'rotate', label: 'Rotating' },
  { value: 'bounce', label: 'Bouncing' },
  { value: 'color-pulse', label: 'Color Pulse' },
  { value: 'pulse', label: 'Pulse' },
  { value: 'shake', label: 'Shake' },
  { value: 'spin', label: 'Spin' },
  { value: 'swing', label: 'Swing' },
  { value: 'float', label: 'Float' },
  { value: 'wiggle', label: 'Wiggle' },
  { value: 'flicker', label: 'Flicker' },
];

const SPECIAL_ANIMATIONS = [
  { value: 'flip-horizontal', label: 'Flip Horizontal' },
  { value: 'flip-vertical', label: 'Flip Vertical' },
  { value: 'rubber-band', label: 'Rubber Band' },
  { value: 'jello', label: 'Jello' },
];

const TEXT_ONLY_ANIMATIONS = [
  { value: 'typewriter', label: 'Typewriter' },
  { value: 'glitch', label: 'Glitch' },
];

const CanvasControls: React.FC = () => {
  const texts = useCanvasStore((s) => s.texts);
  const selectedId = useCanvasStore((s) => s.selectedId);
  const logoProps = useCanvasStore((s) => s.logoProps);
  const backgroundColor = useCanvasStore((s) => s.backgroundColor);
  const setBackgroundColor = useCanvasStore((s) => s.setBackgroundColor);
  const setBgImage = useCanvasStore((s) => s.setBgImage);
  const logoInputRef = useCanvasStore((s) => s.logoInputRef);
  const setLogoImage = useCanvasStore((s) => s.setLogoImage);
  const setSelectedId = useCanvasStore((s) => s.setSelectedId);
  const previewMode = useCanvasStore((s) => s.previewMode);
  const bgImage = useCanvasStore((s) => s.bgImage);
  const setTexts = useCanvasStore((s) => s.setTexts);
  const setLogoProps = useCanvasStore((s) => s.setLogoProps);
  const saveStateToHistory = useCanvasStore((s) => s.saveStateToHistory);
  const canvasImages = useCanvasStore((s) => s.canvasImages);
  const updateImage = useCanvasStore((s) => s.updateImage);
  const { saveHistory } = useCanvasHistory();

  const selectedText = useMemo(() => {
    if (!texts || !selectedId || typeof selectedId !== 'string') return null;
    return texts.find((t) => t.id === selectedId) || null;
  }, [selectedId, texts]);

  const selectedImage = useMemo(() => {
    if (!selectedId || selectedId === 'logo' || selectedId === 'canvas') return null;
    return canvasImages.find((img) => img.id === selectedId);
  }, [selectedId, canvasImages]);

  const updateTextProperty = useCallback(
    <K extends keyof TextItem>(property: K, value: TextItem[K]) => {
      setTexts((prev) => prev.map((t) => (t.id === selectedId ? { ...t, [property]: value } : t)));
    },
    [selectedId, setTexts]
  );

  const updateLogoAnimation = useCallback(
    (animation: AnimationType) => {
      setLogoProps((prev) => ({ ...prev, animation }));
    },
    [setLogoProps]
  );

  const animationOptions = useMemo(
    () => (
      <>
        <option value="none">None</option>
        <optgroup label="Loop Animations">
          {LOOP_ANIMATIONS.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </optgroup>
        <optgroup label="Entrance Animations">
          {ENTRANCE_ANIMATIONS.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </optgroup>
        <optgroup label="Special Animations">
          {SPECIAL_ANIMATIONS.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </optgroup>
        {selectedId && selectedId !== 'logo' && selectedId !== 'canvas' && selectedText && (
          <optgroup label="Text Only">
            {TEXT_ONLY_ANIMATIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </optgroup>
        )}
      </>
    ),
    [selectedId, selectedText]
  );

  const handleBackgroundColorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setBackgroundColor(e.target.value);
    },
    [setBackgroundColor]
  );

  const handleRemoveBg = useCallback(() => {
    setBgImage(null);
  }, [setBgImage]);

  const handleReplaceLogo = useCallback(() => {
    logoInputRef.current?.click();
  }, [logoInputRef]);

  const handleRemoveLogo = useCallback(() => {
    setLogoImage(null);
    setSelectedId(null);
  }, [setLogoImage, setSelectedId]);

  const handleLogoAnimationChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      updateLogoAnimation(e.target.value as AnimationType);
    },
    [updateLogoAnimation]
  );

  const getTextPropertyHandler = useCallback(
    <K extends keyof TextItem>(property: K) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const value =
          property === 'fontSize' || property === 'lineHeight' || property === 'letterSpacing'
            ? Number(e.target.value)
            : e.target.value;
        updateTextProperty(property, value as TextItem[K]);
      },
    [updateTextProperty]
  );

  const handleTextColorChange = (color: string) => {
    saveStateToHistory();
    updateTextProperty('color', color);
  };

  const handleAnimationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    saveStateToHistory();
    if (selectedId === 'logo') {
      updateLogoAnimation(e.target.value as AnimationType);
    } else if (typeof selectedId === 'string') {
      updateTextProperty('animation', e.target.value as AnimationType);
    }
  };

  const handleImageOpacityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!selectedImage) return;

      saveHistory();
      const value = Number.parseFloat(e.target.value);
      updateImage(selectedId as string, { opacity: value } as Partial<CanvasImage>);
    },
    [selectedId, selectedImage, updateImage, saveHistory]
  );

  const handleImageAnimationChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (!selectedImage) return;

      saveHistory();
      const value = e.target.value as AnimationType;
      updateImage(selectedId as string, { animation: value });
    },
    [selectedId, selectedImage, updateImage, saveHistory]
  );

  const handleAnimationPropertyChange = useCallback(
    (property: 'animationDelay' | 'animationDuration', e: React.ChangeEvent<HTMLInputElement>) => {
      if (!selectedImage) return;

      saveHistory();
      // Parse to number and validate range
      const rawValue = Number(e.target.value);
      const value = Number.isNaN(rawValue) ? 0 : Math.max(0, Math.min(property === 'animationDelay' ? 3 : 5, rawValue));

      updateImage(selectedId as string, { [property]: value });
    },
    [selectedId, selectedImage, updateImage, saveHistory]
  );

  const canvasControls = useMemo(
    () =>
      selectedId === 'canvas' ? (
        <div className="edit-controls zngly-controls-panel">
          <div className="control-group">
            <label htmlFor="bg-color-input" className="control-label">
              Background Color:
            </label>
            <input
              id="bg-color-input"
              type="color"
              className="color-picker"
              value={backgroundColor}
              onChange={handleBackgroundColorChange}
            />
          </div>
          {bgImage && (
            <div className="control-group">
              <button
                type="button"
                className="zngly-button zngly-button-tertiary zngly-button-sm"
                onClick={handleRemoveBg}
              >
                Remove Background Image
              </button>
            </div>
          )}
        </div>
      ) : null,
    [selectedId, backgroundColor, bgImage, handleBackgroundColorChange, handleRemoveBg]
  );

  const logoControls = useMemo(
    () =>
      selectedId === 'logo' ? (
        <div className="edit-controls zngly-controls-panel">
          <div className="control-group">
            <div className="control-label" id="logo-controls-label">
              Logo Controls:
            </div>
            <div className="inline-controls" aria-labelledby="logo-controls-label">
              <button
                type="button"
                className="zngly-button zngly-button-tertiary zngly-button-sm"
                onClick={handleReplaceLogo}
              >
                Replace Logo
              </button>
              <button
                type="button"
                className="zngly-button zngly-button-danger zngly-button-sm"
                onClick={handleRemoveLogo}
              >
                Remove Logo
              </button>
            </div>
          </div>

          <div className="control-group">
            <label htmlFor="logo-animation-select" className="control-label">
              Animation:
            </label>
            <select
              id="logo-animation-select"
              className="select-input"
              value={logoProps.animation}
              onChange={handleLogoAnimationChange}
              disabled={previewMode}
            >
              {animationOptions}
            </select>
          </div>
        </div>
      ) : null,
    [
      selectedId,
      logoProps.animation,
      previewMode,
      animationOptions,
      handleReplaceLogo,
      handleRemoveLogo,
      handleLogoAnimationChange,
    ]
  );

  const textControls = useMemo(() => {
    if (!selectedText) return null;

    return (
      <div className="edit-controls zngly-controls-panel">
        <div className="control-group">
          <label htmlFor="text-color-input" className="control-label">
            Text Color:
          </label>
          <input
            id="text-color-input"
            type="color"
            className="color-picker"
            value={selectedText.color}
            onChange={(e) => handleTextColorChange(e.target.value)}
          />
        </div>

        <div className="control-group">
          <label htmlFor="font-family-select" className="control-label">
            Font:
          </label>
          <select
            id="font-family-select"
            className="select-input"
            value={selectedText.fontFamily}
            onChange={getTextPropertyHandler('fontFamily')}
            disabled={previewMode}
          >
            {FONT_FAMILIES.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="font-size-select" className="control-label">
            Font Size:
          </label>
          <select
            id="font-size-select"
            className="select-input"
            value={selectedText.fontSize}
            onChange={getTextPropertyHandler('fontSize')}
            disabled={previewMode}
          >
            {FONT_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}px
              </option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="line-height-select" className="control-label">
            Line Height:
          </label>
          <select
            id="line-height-select"
            className="select-input"
            value={selectedText.lineHeight}
            onChange={getTextPropertyHandler('lineHeight')}
            disabled={previewMode}
          >
            {LINE_HEIGHTS.map((height) => (
              <option key={height} value={height}>
                {height}
              </option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="letter-spacing-select" className="control-label">
            Letter Spacing:
          </label>
          <select
            id="letter-spacing-select"
            className="select-input"
            value={selectedText.letterSpacing}
            onChange={getTextPropertyHandler('letterSpacing')}
            disabled={previewMode}
          >
            {LETTER_SPACINGS.map((spacing) => (
              <option key={spacing} value={spacing}>
                {spacing}px
              </option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="text-align-select" className="control-label">
            Align:
          </label>
          <select
            id="text-align-select"
            className="select-input"
            value={selectedText.align}
            onChange={getTextPropertyHandler('align')}
            disabled={previewMode}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="animation-select" className="control-label">
            Animation:
          </label>
          <select
            id="animation-select"
            className="select-input"
            value={selectedText.animation || 'none'}
            onChange={handleAnimationChange}
            disabled={previewMode}
          >
            {animationOptions}
          </select>
        </div>

        {selectedText.animation && selectedText.animation !== 'none' && (
          <>
            <div className="control-group">
              <label htmlFor="text-animation-duration" className="control-label">
                Duration:
              </label>
              <select
                id="text-animation-duration"
                className="select-input"
                value={selectedText.animationDuration || 1}
                onChange={(e) => {
                  saveStateToHistory();
                  updateTextProperty('animationDuration', Number(e.target.value));
                }}
                disabled={previewMode}
              >
                <option value="0.5">0.5s</option>
                <option value="1">1s</option>
                <option value="1.5">1.5s</option>
                <option value="2">2s</option>
                <option value="2.5">2.5s</option>
                <option value="3">3s</option>
              </select>
            </div>

            <div className="control-group">
              <label htmlFor="text-animation-delay" className="control-label">
                Delay:
              </label>
              <select
                id="text-animation-delay"
                className="select-input"
                value={selectedText.animationDelay || 0}
                onChange={(e) => {
                  saveStateToHistory();
                  updateTextProperty('animationDelay', Number(e.target.value));
                }}
                disabled={previewMode}
              >
                <option value="0">No delay</option>
                <option value="0.2">0.2s</option>
                <option value="0.5">0.5s</option>
                <option value="1">1s</option>
                <option value="1.5">1.5s</option>
                <option value="2">2s</option>
              </select>
            </div>
          </>
        )}
      </div>
    );
  }, [
    selectedText,
    previewMode,
    getTextPropertyHandler,
    handleTextColorChange,
    handleAnimationChange,
    animationOptions,
    updateTextProperty,
    saveStateToHistory,
  ]);

  if (!selectedId || selectedId === 'canvas') {
    return canvasControls;
  }

  if (selectedId && selectedId !== 'logo' && selectedId !== 'canvas' && selectedText) {
    return textControls;
  }

  if (selectedId === 'logo') {
    return logoControls;
  }

  if (selectedImage) {
    const imageOpacity = selectedImage.opacity ?? 1;
    const imageAnimation = selectedImage.animation || 'none';
    const animationDelay = selectedImage.animationDelay ?? 0;
    const animationDuration = selectedImage.animationDuration ?? 1;

    return (
      <fieldset className="edit-controls zngly-controls-panel">
        <legend>Image controls</legend>
        <div className="control-group">
          <label className="control-label" htmlFor="image-opacity">
            Opacity:
          </label>
          <div className="range-with-value">
            <input
              type="range"
              id="image-opacity"
              min="0.1"
              max="1"
              step="0.1"
              value={imageOpacity}
              onChange={handleImageOpacityChange}
              className="range-input"
              disabled={previewMode}
              aria-valuemin={0.1}
              aria-valuemax={1}
              aria-valuenow={imageOpacity}
              aria-valuetext={`${Math.round(imageOpacity * 100)}%`}
            />
            <span className="range-value">{Math.round(imageOpacity * 100)}%</span>
          </div>
        </div>

        <div className="control-group">
          <label className="control-label" htmlFor="image-animation">
            Animation:
          </label>
          <select
            id="image-animation"
            value={imageAnimation}
            onChange={handleImageAnimationChange}
            className="select-input"
            disabled={previewMode}
          >
            {animationOptions}
          </select>
        </div>

        {imageAnimation !== 'none' && (
          <>
            <div className="control-group">
              <label className="control-label" htmlFor="animation-delay">
                Delay (s):
              </label>
              <div className="range-with-value">
                <input
                  type="range"
                  id="animation-delay"
                  min="0"
                  max="3"
                  step="0.1"
                  value={animationDelay}
                  onChange={(e) => handleAnimationPropertyChange('animationDelay', e)}
                  className="range-input"
                  disabled={previewMode}
                  aria-valuemin={0}
                  aria-valuemax={3}
                  aria-valuenow={animationDelay}
                  aria-valuetext={`${animationDelay} seconds`}
                />
                <span className="range-value">{animationDelay}s</span>
              </div>
            </div>

            <div className="control-group">
              <label className="control-label" htmlFor="animation-duration">
                Duration (s):
              </label>
              <div className="range-with-value">
                <input
                  type="range"
                  id="animation-duration"
                  min="0.5"
                  max="5"
                  step="0.1"
                  value={animationDuration}
                  onChange={(e) => handleAnimationPropertyChange('animationDuration', e)}
                  className="range-input"
                  disabled={previewMode}
                  aria-valuemin={0.5}
                  aria-valuemax={5}
                  aria-valuenow={animationDuration}
                  aria-valuetext={`${animationDuration} seconds`}
                />
                <span className="range-value">{animationDuration}s</span>
              </div>
            </div>
          </>
        )}

        <div className="control-group">
          <div className="control-label" id="image-controls-label">
            Image Controls:
          </div>
          <div className="inline-controls" aria-labelledby="image-controls-label">
            <button
              type="button"
              className="zngly-button zngly-button-danger zngly-button-sm"
              onClick={() => {
                saveStateToHistory();
                const id = selectedId as string;
                setSelectedId(null);
                setTimeout(() => updateImage(id, { deleted: true }), 0);
              }}
              disabled={previewMode}
              aria-label="Remove image"
            >
              Remove Image
            </button>
          </div>
        </div>
      </fieldset>
    );
  }

  return null;
};

export default memo(CanvasControls);
